import { useState, useEffect } from "react";
import { formatTimestamp, toDateOnly } from "@/lib/date-utils";
import { DatePicker } from "@/components/ui/date-picker";
import { trpc } from "@/trpc";
import { useAuth } from "@/state/AuthContext";
import { UserType } from "@satyrsmc/shared/lib/enums";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2 } from "lucide-react";
import { ContactSelectCombobox } from "./ContactSelectCombobox";
import { AddContactDialog } from "@/components/contacts/AddContactDialog";
import { EditContactDialog } from "@/components/contacts/EditContactDialog";
import type { Registration, RejectedRegistration } from "@satyrsmc/shared/dto/admin/user";

interface Props {
  registration: Registration | RejectedRegistration | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}

function isRejected(reg: Registration | RejectedRegistration): reg is RejectedRegistration {
  return "rejected_at" in reg;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm">{value ?? <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">
      {children}
    </p>
  );
}

export function RegistrationDetailSheet({ registration, open, onOpenChange, onDone }: Props) {
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [newlyCreatedContactId, setNewlyCreatedContactId] = useState<string | null>(null);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [birthdayOverride, setBirthdayOverride] = useState<string | null | undefined>(undefined);

  const { user } = useAuth();
  const isWebmaster = user?.user_type === UserType.Webmaster;

  const utils = trpc.useUtils();

  const rejected = registration ? isRejected(registration) : false;

  useEffect(() => {
    setBirthdayOverride(undefined);
  }, [registration?.id]);

  const displayedBirthday =
    birthdayOverride !== undefined ? birthdayOverride : (registration?.birthday ?? null);

  const { data: contacts = [] } = trpc.admin.users.listContactsWithoutAccount.useQuery(undefined, {
    enabled: open && !rejected,
  });

  const { data: suggestedContact } = trpc.admin.users.getSuggestedContact.useQuery(
    { registration_id: registration?.id ?? "" },
    { enabled: open && !!registration?.id && !rejected },
  );

  useEffect(() => {
    if (suggestedContact && !selectedContactId) {
      setSelectedContactId(suggestedContact.id);
    }
  }, [suggestedContact]);

  const approveMutation = trpc.admin.users.approveRegistration.useMutation({
    onSuccess: async () => {
      await utils.admin.users.listContactsWithoutAccount.invalidate();
      onDone();
      handleClose();
    },
  });

  const rejectMutation = trpc.admin.users.rejectRegistration.useMutation({
    onSuccess: () => {
      setRejectDialogOpen(false);
      onDone();
      handleClose();
    },
  });

  const unrejectMutation = trpc.admin.users.unrejectRegistration.useMutation({
    onSuccess: () => {
      onDone();
      handleClose();
    },
  });

  const updateRegistrationBirthdayMutation =
    trpc.admin.users.updateRegistrationBirthday.useMutation({
      onSuccess: (updated) => {
        setBirthdayOverride(updated.birthday);
        void utils.admin.users.listRegistrations.invalidate();
        void utils.admin.users.listRejectedRegistrations.invalidate();
      },
    });

  const deleteRegistrationMutation = trpc.admin.users.deleteRegistration.useMutation({
    onSuccess: () => {
      setDeleteDialogOpen(false);
      onDone();
      handleClose();
    },
  });

  const deleteContactMutation = trpc.admin.contacts.delete.useMutation({
    onSuccess: async () => {
      await utils.admin.users.listContactsWithoutAccount.invalidate();
      setSelectedContactId(null);
      setNewlyCreatedContactId(null);
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setSelectedContactId(null);
    setNewlyCreatedContactId(null);
    setEditContactOpen(false);
    setRejectDialogOpen(false);
    setRejectNote("");
    setDeleteDialogOpen(false);
  };

  const handleApprove = () => {
    if (!registration || !selectedContactId) return;
    approveMutation.mutate({
      registration_id: registration.id,
      contact_id: selectedContactId,
    });
  };

  const handleRejectConfirm = () => {
    if (!registration) return;
    rejectMutation.mutate({
      registration_id: registration.id,
      note: rejectNote || undefined,
    });
  };

  const handleUnreject = () => {
    if (!registration) return;
    unrejectMutation.mutate({ registration_id: registration.id });
  };

  const handleContactCreated = async () => {
    await utils.admin.users.listContactsWithoutAccount.invalidate();
    const fresh = await utils.admin.users.listContactsWithoutAccount.fetch();
    const match = fresh.find((c) => c.email?.toLowerCase() === registration?.email?.toLowerCase());
    if (match) {
      setSelectedContactId(match.id);
      setNewlyCreatedContactId(match.id);
    }
  };

  const { data: editContact } = trpc.admin.contacts.get.useQuery(
    { id: newlyCreatedContactId ?? "" },
    { enabled: !!newlyCreatedContactId && editContactOpen },
  );

  if (!registration) return null;

  const name = [registration.first_name, registration.last_name].filter(Boolean).join(" ");
  const isApproving = approveMutation.isPending;
  const isUnrejecting = unrejectMutation.isPending;

  const suggestedName = suggestedContact
    ? [suggestedContact.first_name, suggestedContact.last_name].filter(Boolean).join(" ") ||
      suggestedContact.email
    : null;

  const address = [
    registration.address_line1,
    registration.address_line2,
    [registration.city, registration.state, registration.postal_code].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="right" className="flex w-full max-w-lg flex-col">
          <SheetHeader>
            <SheetTitle>{name || registration.email}</SheetTitle>
            <SheetDescription>
              {rejected
                ? "This registration was rejected."
                : "Review and approve or reject this registration request."}
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-6">
            {/* Rejection banner */}
            {rejected && isRejected(registration) && (
              <section className="rounded-md border border-destructive/30 bg-destructive/5 p-4 space-y-2">
                <p className="text-sm font-semibold text-destructive">Rejected</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Rejected on" value={formatTimestamp(registration.rejected_at)} />
                  <Field label="Reason" value={registration.rejected_reason ?? null} />
                </div>
              </section>
            )}

            {/* Contact info */}
            <section className="space-y-3">
              <SectionHeader>Contact Information</SectionHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" value={registration.email} />
                <Field label="Phone" value={registration.phone ?? null} />
                <Field label="First Name" value={registration.first_name ?? null} />
                <Field label="Last Name" value={registration.last_name ?? null} />
              </div>
            </section>

            {/* Address */}
            <section className="space-y-3">
              <SectionHeader>Address</SectionHeader>
              {address ? (
                <p className="text-sm whitespace-pre-line">{address}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No address provided.</p>
              )}
            </section>

            {/* Personal */}
            <section className="space-y-3">
              <SectionHeader>Personal</SectionHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Date of Birth
                  </p>
                  <div className="mt-0.5">
                    <DatePicker
                      value={toDateOnly(displayedBirthday ?? "")}
                      onChange={(v) =>
                        updateRegistrationBirthdayMutation.mutate({
                          registration_id: registration.id,
                          birthday: v || null,
                        })
                      }
                      disabled={updateRegistrationBirthdayMutation.isPending}
                      className="h-8"
                    />
                  </div>
                </div>
                <Field
                  label="Submitted"
                  value={registration.created_at ? formatTimestamp(registration.created_at) : null}
                />
              </div>
            </section>

            {/* Emergency contact */}
            <section className="space-y-3">
              <SectionHeader>Emergency Contact</SectionHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" value={registration.ice_name ?? null} />
                <Field label="Phone" value={registration.ice_phone ?? null} />
              </div>
            </section>

            {/* Record linkage */}
            <section className="space-y-3">
              <SectionHeader>Record Linkage</SectionHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Contact"
                  value={
                    registration.contact_id ? (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                        Linked
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Not linked</span>
                    )
                  }
                />
                <Field
                  label="Member"
                  value={
                    registration.member_id ? (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                        Linked
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Not linked</span>
                    )
                  }
                />
                <Field
                  label="Source"
                  value={
                    registration.invited_by ? (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Admin invite
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Self-registered</span>
                    )
                  }
                />
              </div>
            </section>

            {/* Contact picker — active registrations only */}
            {!rejected && (
              <section className="space-y-3">
                <SectionHeader>Link to Contact</SectionHeader>
                {newlyCreatedContactId && selectedContactId ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      New contact created. Edit to add details, delete to start over, or approve
                      with the current selection.
                    </p>
                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {contacts.find((c) => c.id === selectedContactId)?.display_name ??
                            "New contact"}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {contacts.find((c) => c.id === selectedContactId)?.email ?? ""}
                        </p>
                      </div>
                      <div className="ml-3 flex shrink-0 gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => setEditContactOpen(true)}
                          disabled={isApproving}
                          aria-label="Edit contact"
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-destructive hover:text-destructive"
                          onClick={() => deleteContactMutation.mutate({ id: selectedContactId })}
                          disabled={isApproving || deleteContactMutation.isPending}
                          aria-label="Delete contact"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Select an existing contact to link this registration to, or create a new one
                      pre-filled with the registrant's details.
                    </p>
                    {suggestedName && !selectedContactId && (
                      <p className="text-sm text-blue-600">Suggested match: {suggestedName}</p>
                    )}
                    <ContactSelectCombobox
                      contacts={contacts}
                      value={selectedContactId}
                      onChange={setSelectedContactId}
                      placeholder="Search contacts..."
                      disabled={isApproving}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAddContactOpen(true)}
                      disabled={isApproving}
                      className="w-full"
                    >
                      + Create new contact
                    </Button>
                  </>
                )}
              </section>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2">
              {rejected ? (
                <Button
                  variant="outline"
                  onClick={handleUnreject}
                  disabled={isUnrejecting}
                  className="w-full"
                >
                  {isUnrejecting ? "Restoring..." : "Un-reject (Restore to Queue)"}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleApprove}
                    disabled={!selectedContactId || isApproving}
                    className="w-full"
                  >
                    {isApproving ? "Approving..." : "Approve Registration"}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={isApproving}
                    className="w-full"
                  >
                    Reject
                  </Button>
                </>
              )}
              {isWebmaster && (
                <Button
                  variant="ghost"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isApproving || isUnrejecting}
                  className="w-full text-destructive hover:text-destructive"
                >
                  Delete Record
                </Button>
              )}
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRejectDialogOpen(false);
            setRejectNote("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject the registration from{" "}
              <strong>{registration?.email}</strong>? They will receive a notification email and
              will not be able to register again with this address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-note">Reason (optional)</Label>
            <Textarea
              id="reject-note"
              placeholder="Optional reason or message for the registrant..."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={3}
              disabled={rejectMutation.isPending}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectNote("");
              }}
              disabled={rejectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Registration</DialogTitle>
            <DialogDescription>
              Permanently delete the registration from <strong>{registration?.email}</strong>? This
              cannot be undone and will not send any notification to the applicant.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteRegistrationMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!registration) return;
                deleteRegistrationMutation.mutate({ registration_id: registration.id });
              }}
              disabled={deleteRegistrationMutation.isPending}
            >
              {deleteRegistrationMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddContactDialog
        open={addContactOpen}
        onOpenChange={setAddContactOpen}
        onSuccess={handleContactCreated}
        defaultFirstName={registration.first_name ?? ""}
        defaultLastName={registration.last_name ?? ""}
        defaultEmail={registration.email ?? ""}
        defaultPhone={registration.phone ?? ""}
        defaultAddressLine1={registration.address_line1 ?? ""}
        defaultAddressLine2={registration.address_line2 ?? ""}
        defaultCity={registration.city ?? ""}
        defaultState={registration.state ?? ""}
        defaultPostalCode={registration.postal_code ?? ""}
        defaultIceName={registration.ice_name ?? ""}
        defaultIcePhone={registration.ice_phone ?? ""}
        defaultBirthday={displayedBirthday ?? ""}
      />

      <EditContactDialog
        open={editContactOpen}
        onOpenChange={setEditContactOpen}
        contact={editContact ?? null}
        onSuccess={() => {
          void utils.admin.users.listContactsWithoutAccount.invalidate();
        }}
      />
    </>
  );
}
